import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MovieRail } from "@/components/MovieRail";
import { toast } from "sonner";
import { Play, Plus, Check, Star, ArrowLeft, Clock, Globe } from "lucide-react";

const MovieDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [movie, setMovie] = useState(null);
  const [related, setRelated] = useState([]);
  const [inList, setInList] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setMovie(null);
    setNotFound(false);
    api.get(`/movies/${id}`)
      .then((r) => setMovie(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!movie) return;
    api.get("/movies", { params: { category: movie.category } })
      .then((r) => setRelated(r.data.filter((m) => m.id !== movie.id)))
      .catch(() => {});
  }, [movie]);

  const checkList = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/mylist");
      setInList(data.some((m) => m.id === id));
    } catch { /* ignore */ }
  }, [user, id]);

  useEffect(() => { checkList(); }, [checkList]);

  const toggleList = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      if (inList) {
        await api.delete(`/mylist/${id}`);
        setInList(false);
        toast("Removed from My List");
      } else {
        await api.post(`/mylist/${id}`);
        setInList(true);
        toast("Added to My List");
      }
    } catch {
      toast.error("Could not update your list");
    }
  };

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#030303] flex flex-col items-center justify-center gap-4 px-6 text-center">
        <p className="text-white/70">This title is no longer available.</p>
        <Link to="/browse" className="rounded-full bg-[#00F0FF] text-black font-semibold px-6 py-2">Back to Browse</Link>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#030303]">
        <div className="w-10 h-10 rounded-full border-2 border-white/10 border-t-[#00F0FF] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#030303] pb-20" data-testid="movie-details">
      <section className="relative h-[60vh] min-h-[420px] w-full">
        <img src={movie.backdrop_url} alt={movie.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/70 to-[#030303]/30" />
        <button onClick={() => navigate(-1)} data-testid="details-back" className="absolute top-20 left-4 sm:left-8 z-10 flex items-center gap-2 text-white/80 hover:text-[#00F0FF] transition-colors">
          <ArrowLeft size={20} /> Back
        </button>
      </section>

      <div className="relative z-10 -mt-40 md:-mt-56 px-4 sm:px-8 max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row gap-6 md:gap-10">
          <motion.img
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            src={movie.poster_url}
            alt={movie.title}
            className="w-40 sm:w-56 aspect-[2/3] object-cover rounded-lg border border-white/10 shadow-2xl shrink-0"
          />
          <div className="flex-1 min-w-0">
            <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tighter text-white text-glow">{movie.title}</h1>

            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="font-mono-ui text-[10px] tracking-widest px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF]">{movie.quality}</span>
              <span className="flex items-center gap-1 text-sm text-white/70"><Star size={14} className="text-[#00F0FF]" fill="currentColor" />{movie.rating?.toFixed(1)}</span>
              <span className="text-sm text-white/70">{movie.release_year}</span>
              <span className="flex items-center gap-1 text-sm text-white/70"><Clock size={14} />{movie.duration}</span>
              <span className="flex items-center gap-1 text-sm text-white/70"><Globe size={14} />{movie.origin}</span>
              <span className="text-xs uppercase tracking-[0.2em] text-white/50">{movie.genre}</span>
            </div>

            <p className="mt-5 text-sm md:text-base text-white/70 max-w-2xl leading-relaxed">{movie.description}</p>

            <div className="flex flex-wrap items-center gap-3 mt-8">
              <Link to={`/watch/${movie.id}`} data-testid="details-play-btn" className="inline-flex items-center gap-2 rounded-full bg-[#00F0FF] text-black font-semibold px-8 py-3 hover:scale-105 transition-transform glow-cyan">
                <Play size={20} fill="currentColor" /> Play
              </Link>
              <button onClick={toggleList} data-testid="details-mylist-btn" className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/20 backdrop-blur-md text-white font-medium px-8 py-3 hover:bg-white/20 transition-colors">
                {inList ? <><Check size={20} className="text-[#00E676]" /> In My List</> : <><Plus size={20} /> My List</>}
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16">
          <MovieRail title="More Like This" movies={related} testid="rail-related" />
        </div>
      </div>
    </div>
  );
};

export default MovieDetails;
